import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useContext, useState } from "react";
import { Tooltip } from "@mui/material";
import { Skeleton } from "@mui/material";
import AnimatedNumber from "../../../Component/AnimatedNumber/AnimatedNumber";
import BrandIndexBreakups from "../../../Component/BrandIndexHistory/BrandIndexBreakupsNewV2";
import BrandIndexBreakupsNewV3 from "../../../Component/BrandIndexHistory/BrandIndexBreakupsNewV3";
import DirectIndexScoreCard from "../../../Component/DirectIndexScoreCard/DirectIndexScoreCard";
import CompositeIndexScoreChart from "../../../Component/CompositeIndexScoreChart/CompositeIndexScoreChart";
import ErrorView from "../../../Component/ErrorView/ErrorView";
import Modal from "../../../Component/Modal/Modal";
import MUICard from "../../../Component/UI/MUICard/MUICard";
import MUIIconButton from "../../../Component/UI/MUIIconButton/MUIIconButton";
import WeightedDonutChart from "../../../Component/WeightedDonutChart/WeightedDonutChart";
import { DiscoveryContext } from "../discovery-context";
import ProductVariant from "../ProductVariant";
import classes from "./CompetitorItem.module.css";
import { ENTITY_LABELS } from "../../../utils/labelMapping";
import BrandAvatar from "../../../Component/Avatar/Avatar";
import {
  calcBrandIndexScore,
  calcMultiFactorIndexScore,
  calcMultiFactorCompositeScore,
  getBrandPillarsAvg,
  getBrandPillarsDetail,
  getProductDimensionsAvg,
  getPurchaseCriteriaAvg,
  getSentimentScoreByFactor,
  getOrderedAccessibilityFactors,
  getOrderedCompositeFactors,
  calcDirectIndexScoreForEntity,
  isAccessibilityIndexScoreType,
  isCompositeIndexScoreType,
  RBI_COLORS,
  RBI_WEIGHTS,
  getFactorColor,
} from "../../../utils/brandIndexCalc";

function extractDomain(input) {
  if (/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(input)) {
    return input;
  }
  try {
    if (!/^https?:\/\//i.test(input)) {
      input = "http://" + input;
    }
    const urlObject = new URL(input);
    let hostname = urlObject.hostname;
    const parts = hostname.split(".");

    if (parts.length > 2) {
      hostname = parts.slice(-2).join(".");
    }

    return hostname;
  } catch (error) {
    console.error("Invalid input:", error.message);
    return null;
  }
}

export default function CompetitorItem({
  competitor,
  brandIndex,
  isHighestFavorability= false,
  brand,
  index,
  onRetry = () => {},
}) {
  const [showBrandIndexBreakups, setShowBrandIndexBreakups] = useState(false);
  const [showDirectIndexBreakups, setShowDirectIndexBreakups] = useState(false);
  const searchParams = new URLSearchParams(window.location.search);
  const isReporting = searchParams.get("isReporting") === "true";

  const discoveryCtx = useContext(DiscoveryContext);
  const isAccessibility = isAccessibilityIndexScoreType(discoveryCtx.customIndexScoreType);
  const isComposite = isCompositeIndexScoreType(discoveryCtx.customIndexScoreType);
  const isMultiFactorMode = isAccessibility || isComposite;
  const category = discoveryCtx.category;
  const llm = discoveryCtx.llm;
  const market = discoveryCtx.market;
  const selectedInput = {
        llm,
        market,
        category,
        brand: discoveryCtx.entity,
        product: null,
      }

  const ctxCompetitor = discoveryCtx.competitors
    .filter((comp) => comp.name != brand)
    .find((comp) => comp.name === competitor);

  let domain = "";
  if (ctxCompetitor && ctxCompetitor?.domain) {
    domain = extractDomain(ctxCompetitor?.domain);
  }

  const variants = ctxCompetitor?.variants
    ? ctxCompetitor.variants.filter((variant) => variant.active)
    : [];
  let cardContent = "";
  let loading = true;
  let hasError = false;
  const transactionStatus = discoveryCtx.state.transactionStatus;
  if (competitor && brandIndex?.total) {
    loading = false;
    cardContent = (
      <>
        {!competitor && (
          <div className={classes.add_container}>
            <MUIIconButton size="large">
              <AddCircleOutlineOutlinedIcon
                style={{ fontSize: 40 }}
                fontSize="large"
              />
            </MUIIconButton>
          </div>
        )}
        {!isComposite && brandIndex?.extras && (
          <MUIIconButton
            data-html2canvas-ignore
            onClick={() => setShowBrandIndexBreakups(true)}
            className={classes.settings}
            id={isHighestFavorability ? "competitor_sentiment" : undefined}
          >
            <InfoOutlinedIcon />
          </MUIIconButton>
        )}
        
        <div className={classes.header}>
          <Tooltip title={brandIndex?.name} placement="top">
          {/* <img
            data-html2canvas-ignore
            className={classes.logo}
            src={`https://img.logo.dev/${domain}?token=pk_SaDf4t2eRsW5Fc84zgJRZA`}
          /> */}
           <BrandAvatar
             domainName={domain}
             brandName={competitor}
           />
          </Tooltip>
           <Tooltip title={brandIndex?.name} placement="top">
          <div className={classes.title_container}>
            <span className={classes.title}>{competitor}</span>
            {variants.length > 0 && (
              <ProductVariant variants={variants} editable={false}>
                <span className={classes.variant_text}>
                  {variants.length} variants
                </span>
              </ProductVariant>
            )}
          </div>
           </Tooltip>
        </div>
        <div className={classes.content}>
          {(() => {
            const isProduct = discoveryCtx.type === "product";
            const brandPillarsAvg     = isProduct
              ? getProductDimensionsAvg(competitor, discoveryCtx.taxonomies)
              : getBrandPillarsAvg(competitor, discoveryCtx.core_attributes);
            const purchaseCriteriaAvg = getPurchaseCriteriaAvg(competitor, discoveryCtx.category_dimensions);

            const sentimentScoreByFactor = isMultiFactorMode
              ? getSentimentScoreByFactor(competitor, brandIndex?.extras?.favorabilities)
              : null;
            const multiFactorFactors = isComposite
              ? getOrderedCompositeFactors(sentimentScoreByFactor, brandIndex?.extras?.productMappingAttribute)
              : getOrderedAccessibilityFactors(sentimentScoreByFactor, brandIndex?.extras?.productMappingAttribute);
            const competitorRankRow = brandIndex?.extras?.rank?.find(
              (r) => r.name?.toLowerCase().trim() === competitor?.toLowerCase().trim()
            ) ?? brandIndex?.extras?.rank?.[0];
            const multiFactorArgs = {
              factors: multiFactorFactors,
              getRankForFactor: (factor) => competitorRankRow?.rankScoreByFactor?.[factor]?.score ?? null,
              getSentimentForFactor: (factor) => sentimentScoreByFactor?.[factor]?.normalizedScore ?? null,
              getPillarForFactor: (factor) =>
                getBrandPillarsDetail(competitor, discoveryCtx.core_attributes)
                  .find((a) => a.name === factor)?.score ?? null,
            };
            const multiFactor = isMultiFactorMode && multiFactorFactors.length
              ? (isComposite ? calcMultiFactorCompositeScore(multiFactorArgs) : calcMultiFactorIndexScore(multiFactorArgs))
              : null;

            let compositeSortOrder;
            if (isComposite && multiFactor) {
              const mainBrandName = discoveryCtx.entity;
              const mainBrandScore = discoveryCtx.brand_scores?.[0];
              const mainSentimentScoreByFactor = mainBrandName
                ? getSentimentScoreByFactor(mainBrandName, mainBrandScore?.extras?.favorabilities)
                : null;
              const mainFactors = mainSentimentScoreByFactor
                ? getOrderedCompositeFactors(mainSentimentScoreByFactor, mainBrandScore?.extras?.productMappingAttribute)
                : [];
              if (mainFactors.length) {
                const mainRankRow = mainBrandScore?.extras?.rank?.find(
                  (r) => r.name?.toLowerCase().trim() === mainBrandName?.toLowerCase().trim()
                ) ?? mainBrandScore?.extras?.rank?.[0];
                const mainMultiFactor = calcMultiFactorCompositeScore({
                  factors: mainFactors,
                  getRankForFactor: (factor) => mainRankRow?.rankScoreByFactor?.[factor]?.score ?? null,
                  getSentimentForFactor: (factor) => mainSentimentScoreByFactor?.[factor]?.normalizedScore ?? null,
                  getPillarForFactor: (factor) =>
                    getBrandPillarsDetail(mainBrandName, discoveryCtx.core_attributes)
                      .find((a) => a.name === factor)?.score ?? null,
                });
                compositeSortOrder = [...mainMultiFactor.breakdown]
                  .sort((a, b) => (b.total ?? 0) - (a.total ?? 0))
                  .map((f) => f.factor);
              }
            }

            const directScore = isComposite
              ? calcDirectIndexScoreForEntity({
                  rankScoreByFactor: competitorRankRow?.rankScoreByFactor,
                  sentimentScoreByFactor,
                  brandPillarsDetail: isProduct ? [] : getBrandPillarsDetail(competitor, discoveryCtx.core_attributes),
                })
              : null;

            const { total: rbiTotal, visNorm, sentNorm, pillarsNorm, purchaseNorm, contributions } =
              multiFactor
                ? { total: multiFactor.total }
                : calcBrandIndexScore({
                    rank: brandIndex?.rank,
                    favorability: brandIndex?.favorability,
                    brandPillarsAvg,
                    purchaseCriteriaAvg,
                  });


            const metricLabel = (key) =>
              key === "visibility" ? ENTITY_LABELS.rank
                : key === "sentiment" ? "Sentiment"
                : isProduct ? ENTITY_LABELS.productDimension : ENTITY_LABELS.brandPillar;

            // Accessibility mode is grouped factor-first: one card/segment per
            // factor, each carrying its own visibility/sentiment/brand-pillar
            // sub-breakdown (rather than one card per metric).
            const SCORE_CARDS = multiFactor
              ? multiFactor.breakdown.map((f, i) => ({
                  key: f.factor,
                  label: f.factor,
                  weightPct: Math.round(f.perFactorMax),
                  raw: f.total.toFixed(1),
                  max: Math.round(f.perFactorMax),
                  color: getFactorColor(i),
                }))
              : [
                  { key: "visibility",       label: ENTITY_LABELS.rank,                             weight: RBI_WEIGHTS.visibility,       raw: brandIndex?.rank,         max: 50, contribution: contributions.visibility },
                  { key: "sentiment",        label: "Sentiment",                                    weight: RBI_WEIGHTS.sentiment,        raw: brandIndex?.favorability, max: 50, contribution: contributions.sentiment },
                  { key: "brandPillars",     label: isProduct ? ENTITY_LABELS.productDimension : ENTITY_LABELS.brandPillar, weight: RBI_WEIGHTS.brandPillars, raw: brandPillarsAvg,          max: 10, contribution: contributions.brandPillars },
                  { key: "purchaseCriteria", label: ENTITY_LABELS.purchaseCriteria,                 weight: RBI_WEIGHTS.purchaseCriteria, raw: purchaseCriteriaAvg,      max: 10, contribution: contributions.purchaseCriteria },
                ];

            const donutSegments = multiFactor
              ? multiFactor.breakdown.map((f, i) => ({
                  key: f.factor,
                  label: f.factor,
                  color: getFactorColor(i),
                  sliceValue: Math.round(f.perFactorMax),
                  displayValue: f.total.toFixed(1),
                  weightPct: Math.round(f.perFactorMax),
                  sub: [
                    { label: metricLabel("visibility"), raw: f.visRaw, norm: f.visNorm, max: Math.round(f.perFactorMax) },
                    { label: metricLabel("sentiment"), raw: f.sentRaw, norm: f.sentNorm, max: Math.round(f.perFactorMax) },
                    { label: metricLabel("brandPillars"), raw: f.pillarRaw, norm: f.pillarsNorm, max: Math.round(f.perFactorMax) },
                  ],
                }))
              : undefined;

            return (
              <>
                {isComposite && (
                  <div style={{ width: "100%", marginTop: 10 }}>
                    <DirectIndexScoreCard
                      directScore={directScore}
                      onInfoClick={() => setShowDirectIndexBreakups(true)}
                      size="small"
                    />
                  </div>
                )}
              {isComposite && multiFactor ? (

                <div style={{ width: "100%", padding: "0 4px" }}>
                  <CompositeIndexScoreChart
                    multiFactor={multiFactor}
                    size="small"
                    onInfoClick={brandIndex?.extras ? () => setShowBrandIndexBreakups(true) : undefined}
                    iconId={isHighestFavorability ? "competitor_sentiment" : undefined}
                    sortOrder={compositeSortOrder}
                  />
                </div>
              ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "0 4px" }}>
                <div style={{ flex: "0 0 58%", minWidth: 0 }}>
                  <WeightedDonutChart
                    total={rbiTotal}
                    visNorm={visNorm}
                    sentNorm={sentNorm}
                    pillarsNorm={pillarsNorm}
                    purchaseNorm={purchaseNorm}
                    size="medium"
                    showLegend={false}
                    segments={donutSegments}
                    {...(multiFactor ? { outOf: Math.round(multiFactor.perFactorMax * multiFactor.breakdown.length) } : {})}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                  {SCORE_CARDS.map((c) => {
                    const color = multiFactor ? c.color : RBI_COLORS[c.key];
                    const weightPct = multiFactor ? c.weightPct : Math.round(c.weight * 100);
                    const rawDisplay = multiFactor
                      ? c.raw
                      : c.raw != null ? (c.max === 10 ? Number(c.raw).toFixed(1) : c.raw) : "—";
                    return (
                      <div key={c.key} className="competitor-score-row" style={{ background: "#fff", borderRadius: 8, padding: "8px 8px", border: `1.5px solid ${color}33`, display: "flex", alignItems: "center", gap: 6 }}>
                        {/* Left: dot + label + weight */}
                        <div style={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0, gap: 5 }}>
                          <span style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: color, flexShrink: 0, display: "inline-block" }} />
                          <Tooltip title={c.label} placement="top" enterDelay={400}>
                            <span className="competitor-score-label" style={{ fontSize: 11, fontWeight: 600, color: "#2d3748", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.label}</span>
                          </Tooltip>
                          <span className="competitor-weight-pct" style={{ fontSize: 10, color: "#8a94a6", flexShrink: 0 }}>{weightPct}%</span>
                        </div>
                        {/* Right: score */}
                        <div className="competitor-score-value" style={{ flexShrink: 0, textAlign: "right" }}>
                          <span style={{ fontSize: 18, fontWeight: 700, color }}>{rawDisplay}</span>
                          <span style={{ fontSize: 10, color: "#8a94a6" }}>/{c.max}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              )}
              </>
            );
          })()}
        </div>
      </>
    );
  } else if(transactionStatus === "RETRY"){
    hasError = true;
    loading = false;
  } else if(transactionStatus === "RUNNING"){
    hasError= false;
    loading = true;
  }

  if (competitor && hasError) {
    cardContent = (
      <ErrorView
        title="Error Fetching Competitor"
        message="Request Failed"
      />
    );
  }

  let compeititorCard = "";
  if (cardContent) {
    compeititorCard = (
      <MUICard
        variant="elevated"
        elevation={0}
        borderRadius={3}
        className={classes.card}
        width={"90%"}
      >
        {cardContent}
      </MUICard>
    );
  }

  return (
    <div
      className={classes.container}
      style={{
        backgroundColor: compeititorCard ? "#eff4f8" : "inherit",
      }}
    >
      <Modal
        width={1200}
        open={showBrandIndexBreakups}
        handleClose={() => setShowBrandIndexBreakups(false)}
      >
        <BrandIndexBreakups
          data={brandIndex?.extras}
          isOpen={showBrandIndexBreakups}
          type={discoveryCtx.type}
          selectedInput={selectedInput}
          brandProduct={ctxCompetitor?.name}
        />
      </Modal>
      {isCompositeIndexScoreType(discoveryCtx.customIndexScoreType) && (
        <Modal
          width={1200}
          open={showDirectIndexBreakups}
          handleClose={() => setShowDirectIndexBreakups(false)}
        >
          <BrandIndexBreakupsNewV3
            data={brandIndex?.extras}
            brandProduct={competitor}
            selectedInput={selectedInput}
          />
        </Modal>
      )}
      {compeititorCard}
      {competitor && !hasError && loading && (
        <Skeleton
          variant="rectangular"
          style={{
            zIndex: 1,
            flexGrow: 1,
            height: 320,
            flexBasis: "calc(50% - 12.5px)",
            backgroundColor: "rgb(0 0 0 / 11%)",
          }}
        />
      )}
    </div>
  );
}
